import BaseApplicationApi from 'client/api/v2/BaseApplicationApi';
import axios from 'axios';

class ApplicationApiV2 extends BaseApplicationApi {
  constructor() {
    super('/api/v2/applications', {
      skip: 0,
      limit: 50,
      sort: 'displayLabel', // Prepend with a '-' to reverse default sort direction
      search: '',
    });
  }

  fetchUserGroups(id, params = { skip: 0, limit: 50 }) {
    const boundUserGroupsUrl = `${this.baseUrl}/${id}/usergroups`;
    return this.initialize()
      .then(() => axios.get(boundUserGroupsUrl, params))
      .then(({ data }) => data);
  }

  // Applications api v2 doesn't have DELETE method.
  // This is the same method than the base class but
  // the URL is hardcoded to use v1 api delete method
  async delete(id) {
    await this.initialize();
    return axios.delete(`/api/applications/${id}`);
  }

  /**
   * Fetches all applications from a provided list in increments of 100.
   * @param string[] Application Ids
   */
  fetchApplicationsById(ids) {
    const maxFetchLimit = 100;
    const chunkedIds = [];

    if (ids.length > maxFetchLimit) {
      for (let i = 0; i < ids.length; i += maxFetchLimit) {
        const chunk = ids.slice(i, i + maxFetchLimit);
        chunkedIds.push(chunk);
      }
    } else {
      chunkedIds.push(ids);
    }

    const callApi = (data = []) => this.fetch(
      0,
      maxFetchLimit,
      'displayLabel',
      undefined,
      undefined,
      [{ name: '_id', operator: 'in', value: data.join('|') }],
    );

    const requests = chunkedIds.map(chunk => callApi(chunk));

    return Promise.all(requests)
      .then(responses => responses
        .map(response => response.results)
        .reduce((all, data) => all.concat(data), [])
        .map(app => ({ ...app, id: app._id })));
  }

  postSPMetadata(xml, model) {
    const mesg = 'Something went wrong - the service provider metadata file could not be read';
    const data = { spConfiguration: model, spMetadata: xml };

    return this.initialize()
      .then(() => axios.post('/api/applications/metadata', data))
      .then((resp) => {
        if (resp && resp.data) return resp.data;
        throw new Error(`no data in response: ${resp}`);
      })
      .catch(() => {
        throw new Error(mesg);
      });
  }

  /**
   * Deleting application custom logo
   * @param {ObjectId} id Application Id
   */
  deleteCustomLogo(id) {
    return this
      .initialize()
      .then(() => axios.delete(`${this.baseUrl}/${id}/logo`, {}));
  }

  /**
   * Uploading application custom logo
   * @param {ObjectId} id Application Id
   * @param {ObjectId} file Application Custom Logo
   */
  uploadCustomLogo({ id, file }) {
    const formData = new FormData();
    formData.append('image', file);

    return this
      .initialize()
      .then(() => axios.post(`${this.baseUrl}/${id}/logo`, formData));
  }

  /**
   * Application provisioning using SCIM
   * @param {ObjectId} id Application Id
   * @param {Boolean} groupsEnabled Application Id
   * @param {String} keyValue Service provider token key
   * @param {String} urlValue Service provider vanity URL
   * @param {String} scimVersion Scim version (v1 | v2)
   * @param {Array} translationRules Application translation rules array
   */
  SCIMPostProvisionActivate(
    id,
    groupsEnabled = false,
    keyValue = '',
    urlValue = '',
    scimVersion = 'v2',
    translationRules = [],
  ) {
    return this
      .initialize()
      .then(() => axios.post(`${this.baseUrl}/${id}/provision/activate`, {
        groups_enabled: groupsEnabled,
        api_key: keyValue,
        base_url: urlValue,
        scim_version: scimVersion,
        translation_rules: translationRules,
      }));
  }

  /**
   * Update Application provisioning using SCIM
   * @param {ObjectId} id Application Id
   * @param {ObjectId} state server state id
   * @param {String} vanityUrl Service provider vanity URL
   */
  SCIMPutProvisionActivate(id, state, vanityUrl = '') {
    return this
      .initialize()
      .then(() => axios.put(`${this.baseUrl}/${id}/provision/activate`, {
        state,
        vanity_url: vanityUrl,
      }));
  }

  /**
   * Application provisioned using SCIM
   * @param {ObjectId} id Application Id
   */

  /**
   * NOTE: adding a succesful status code range as some reqs are returning 200
   * while other reqs made to cached resources are returning 300 range status codes
   */
  SCIMGetProvisionActivate(id) {
    return this
      .initialize()
      .then(() => axios.get(`${this.baseUrl}/${id}/provision`, {
        validateStatus: status => status >= 200 && status <= 399,
      }));
  }

  /**
   * Application provisioned using SCIM
   * @param {ObjectId} id Application Id
   * @param {Boolean} groupsEnabled
   * @param {String} vanityUrl
   * @param {String} scimVersion
   * @param {Array} translationRules
   */
  SCIMPutProvision(
    id,
    apiKey = '',
    groupsEnabled = false,
    vanityUrl = null,
    scimVersion = 'v2',
    translationRules = [],
  ) {
    return this
      .initialize()
      .then(() => axios.put(`/api/v2/applications/${id}/provision`, {
        api_key: apiKey,
        groups_enabled: groupsEnabled,
        scim_version: scimVersion,
        base_url: (vanityUrl === '' ? null : vanityUrl),
        translation_rules: translationRules,
      }));
  }

  /**
   * Deactivate SCIM for Application
   * @param {ObjectId} id Application Id
   */
  SCIMDeleteProvisionActive(id) {
    return this
      .initialize()
      .then(() => axios.delete(`${this.baseUrl}/${id}/provision`, {}));
  }

  /**
   * Finish SCIM activation for Application
   * @param {Object} Options
   * @param {String} Options.apiKey API Key from SP
   * @param {String} Options.appName Application template name
   * @param {String} Options.state Id of flow state started by SCIMPostProvisionActivate
   * @param {String} Options.vanityUrl Vanity URL from the SP
   */
  SCIMPostProvisionCallback({
    apiKey = '',
    appName,
    groupsEnabled = false,
    state,
    vanityUrl = '',
    clientId,
    clientSecret,
  }) {
    let payload = {
      api_key: apiKey.trim(),
      groups_enabled: groupsEnabled,
      state,
      vanity_url: vanityUrl.trim(),
    };

    if (clientId && clientSecret) {
      payload = {
        state,
        client_id: clientId,
        client_secret: clientSecret,
        groups_enabled: false,
      };
    }

    return this
      .initialize()
      .then(() => axios.post(`/api/v2/provision/${appName}/callback`, payload));
  }

  /**
   * Test credentials for SCIM generic connector
   * @param {String} Options
   * @param {String} Options.apiKey API Key from SP
   * @param {String} Options.baseUrl Vanity URL from the SP
   * @param {String} Options.scimVersion
   */
  SCIMPostVerify({
    apiKey = '',
    baseUrl = '',
    scimVersion = 'v2',
    groupsEnabled = true,
  }) {
    return this
      .initialize()
      .then(() => axios.post('/api/v2/provision/client/verify',
        {
          api_key: apiKey,
          base_url: baseUrl.trim(),
          scim_version: scimVersion,
          groups_enabled: groupsEnabled,
        }));
  }
}

export default new ApplicationApiV2();
